import {IAttachmentSummary} from '../../../models/conversation.model';

const MAX_FILE_NAME_LENGTH = 180;
const MAX_CONTENT_TYPE_LENGTH = 120;

export function readConversationAttachmentSummaries(message: any): IAttachmentSummary[] {
  const summaries = Array.isArray(message?.attachmentSummaries)
    ? message.attachmentSummaries
    : Array.isArray(message?.AttachmentSummaries)
      ? message.AttachmentSummaries
      : [];

  return summaries.map((attachment: any) => ({
    attachmentId: readAttachmentId(attachment?.attachmentId ?? attachment?.AttachmentId),
    expiresAtUtc: attachment?.expiresAtUtc ?? attachment?.ExpiresAtUtc,
    fileName: sanitizeText(attachment?.fileName ?? attachment?.FileName, MAX_FILE_NAME_LENGTH),
    contentType: sanitizeText(attachment?.contentType ?? attachment?.ContentType, MAX_CONTENT_TYPE_LENGTH),
    size: typeof attachment?.size === 'number'
      ? Math.max(0, attachment.size)
      : typeof attachment?.Size === 'number'
        ? Math.max(0, attachment.Size)
        : 0
  }));
}

function readAttachmentId(value: unknown): string | undefined {
  return typeof value === 'string' && /^[a-f0-9]{64}$/.test(value) ? value : undefined;
}

export function getConversationAttachmentDisplayName(attachment: IAttachmentSummary, index: number): string {
  const fileName = `${attachment?.fileName ?? ''}`.trim();
  return fileName || `Uploaded file ${index + 1}`;
}

export function getConversationAttachmentMetaText(attachment: IAttachmentSummary): string {
  const parts: string[] = [];
  const typeLabel = getConversationAttachmentTypeLabel(attachment?.contentType);
  if (typeLabel) {
    parts.push(typeLabel);
  }

  const size = typeof attachment?.size === 'number' ? attachment.size : 0;
  if (size > 0) {
    parts.push(formatBytes(size));
  }

  return parts.join(' · ');
}

function getConversationAttachmentTypeLabel(contentType: unknown): string {
  const trimmed = `${contentType ?? ''}`.trim().toLowerCase();
  if (!trimmed) {
    return '';
  }

  const subtype = trimmed.split('/')[1] || trimmed;
  return subtype.replace(/\+/g, ' ').toUpperCase();
}

function formatBytes(size: number): string {
  if (!Number.isFinite(size) || size <= 0) {
    return '';
  }

  if (size < 1024) {
    return `${size} B`;
  }

  const units = ['KB', 'MB', 'GB'];
  let value = size / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
}

function sanitizeText(value: unknown, maxLength: number): string {
  const text = Array.from(`${value ?? ''}`)
    .filter(character => {
      const code = character.charCodeAt(0);
      return code >= 0x20 && code !== 0x7f;
    })
    .join('')
    .trim();
  return text.length <= maxLength ? text : text.slice(0, maxLength).trimEnd();
}
